import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { MotoristaProfile, MotoristaStats, ComprovanteEntregaData, OcorrenciaItem, VistoriaVeiculo } from '../types/areaMotorista';
import { OrdemDeServico } from '../types/ordemServico';
import { areaMotoristaService } from '../services/areaMotoristaService';
import { rastreamentoNotificacaoService, NotificacaoItem } from '../services/rastreamentoNotificacaoService';
import { auth } from '../lib/firebase';

interface AreaMotoristaContextType {
  motoristasList: MotoristaProfile[];
  currentMotorista: MotoristaProfile | null;
  setCurrentMotoristaId: (id: number) => void;
  isAgregado: boolean;
  /** true quando o e-mail logado não corresponde a nenhum motorista cadastrado */
  semVinculo: boolean;
  stats: MotoristaStats;
  ordensServico: OrdemDeServico[];
  viagemAtiva: OrdemDeServico | null;
  setViagemAtiva: (os: OrdemDeServico | null) => void;
  isLoading: boolean;
  isSaving: boolean;
  refreshData: () => Promise<void>;
  
  // Notificações e Rastreamento GPS
  notificacoes: NotificacaoItem[];
  notificacaoPendenteAlerta: NotificacaoItem | null;
  dispensarAlerta: (id: number) => Promise<void>;
  enviarLocalizacaoGps: () => Promise<{ success: boolean; message?: string }>;
  transmitindoGps: boolean;

  // Ações de execução da viagem
  iniciarViagem: (osId: number) => Promise<boolean>;
  chegadaColeta: (osId: number) => Promise<boolean>;
  iniciarTransporte: (osId: number) => Promise<boolean>;
  chegadaEntrega: (osId: number) => Promise<boolean>;
  finalizarEntrega: (comprovante: ComprovanteEntregaData) => Promise<boolean>;
  registrarOcorrencia: (ocorrencia: OcorrenciaItem) => Promise<boolean>;
  salvarVistoria: (vistoria: VistoriaVeiculo) => Promise<boolean>;
}

const AreaMotoristaContext = createContext<AreaMotoristaContextType | undefined>(undefined);

const INITIAL_STATS: MotoristaStats = {
  viagensHoje: 0,
  viagensEmAndamento: 0,
  viagensConcluidas: 0,
  totalViagensMes: 0,
  totalGanhosMes: 0,
  totalRepassesPendentes: 0,
  totalRepassesPagos: 0,
  kmRodadoAproximado: 0
};

export const AreaMotoristaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [motoristasList, setMotoristasList] = useState<MotoristaProfile[]>([]);
  const [currentMotorista, setCurrentMotorista] = useState<MotoristaProfile | null>(null);
  const [stats, setStats] = useState<MotoristaStats>(INITIAL_STATS);
  const [ordensServico, setOrdensServico] = useState<OrdemDeServico[]>([]);
  const [viagemAtiva, setViagemAtiva] = useState<OrdemDeServico | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [semVinculo, setSemVinculo] = useState<boolean>(false);

  // Estados de Notificações e GPS
  const [notificacoes, setNotificacoes] = useState<NotificacaoItem[]>([]);
  const [notificacaoPendenteAlerta, setNotificacaoPendenteAlerta] = useState<NotificacaoItem | null>(null);
  const [transmitindoGps, setTransmitindoGps] = useState<boolean>(false);

  // Carrega lista inicial de motoristas e detecta usuário atual
  useEffect(() => {
    async function init() {
      setIsLoading(true);
      try {
        const motoristas = await areaMotoristaService.getMotoristas();
        setMotoristasList(motoristas);

        // Tenta detectar motorista logado por e-mail do Firebase Auth
        const userEmail = auth.currentUser?.email;

        let motoristaEncontrado: MotoristaProfile | null = null;

        if (userEmail && motoristas.length > 0) {
          motoristaEncontrado = motoristas.find(m => m.email?.toLowerCase() === userEmail.toLowerCase()) || null;
        }

        // IMPORTANTE: sem correspondência exata de e-mail, NÃO seleciona um motorista
        // qualquer da lista — isso expunha ganhos/repasses/extrato de qualquer
        // motorista a qualquer usuário autenticado sem vínculo.
        if (!motoristaEncontrado) {
          setSemVinculo(true);
        }

        setCurrentMotorista(motoristaEncontrado);
      } catch (err) {
        console.error('Erro na inicialização da Área do Motorista:', err);
      } finally {
        setIsLoading(false);
      }
    }

    init();
  }, []);

  // Recarrega dados do motorista selecionado
  const refreshData = useCallback(async () => {
    if (!currentMotorista) return;
    setIsLoading(true);
    try {
      const [osData, statsData] = await Promise.all([
        areaMotoristaService.getOrdensServico(currentMotorista.id),
        areaMotoristaService.getStats(currentMotorista.id)
      ]);

      setOrdensServico(osData);
      setStats(statsData);

      // Define como viagem ativa a primeira OS em andamento (status_id = 2 ou 1 de hoje)
      const emAndamento = osData.find(os => os.status_id === 2);
      if (emAndamento) {
        setViagemAtiva(emAndamento);
      }
    } catch (err) {
      console.error('Erro ao atualizar dados do motorista:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentMotorista]);

  useEffect(() => {
    if (currentMotorista) {
      refreshData();
    }
  }, [currentMotorista, refreshData]);

  // Troca de motorista selecionado
  const setCurrentMotoristaId = useCallback((id: number) => {
    const found = motoristasList.find(m => m.id === id);
    if (found) {
      setCurrentMotorista(found);
    }
  }, [motoristasList]);

  // Determina se é Agregado (vê repasses) ou Motorista CLT/Próprio (não vê valores financeiros)
  const isAgregado = Boolean(
    currentMotorista?.tipo_vinculo?.toLowerCase().includes('agregado') ||
    currentMotorista?.tipo_vinculo?.toLowerCase().includes('autônomo') ||
    currentMotorista?.tipo_vinculo?.toLowerCase().includes('autonomo')
  );

  // Ações de execução da viagem
  const iniciarViagem = async (osId: number): Promise<boolean> => {
    setIsSaving(true);
    // Transmitir localização imediatamente ao iniciar o trajeto
    enviarLocalizacaoGps().catch(console.error);
    const ok = await areaMotoristaService.atualizarStatusOS(osId, 2, 'Início de Deslocamento / Viagem Iniciada');
    if (ok) {
      await refreshData();
    }
    setIsSaving(false);
    return ok;
  };

  const chegadaColeta = async (osId: number): Promise<boolean> => {
    setIsSaving(true);
    const ok = await areaMotoristaService.atualizarStatusOS(osId, 2, 'Chegada no Local de Coleta / Aguardando Carregamento');
    if (ok) await refreshData();
    setIsSaving(false);
    return ok;
  };

  const iniciarTransporte = async (osId: number): Promise<boolean> => {
    setIsSaving(true);
    const ok = await areaMotoristaService.atualizarStatusOS(osId, 2, 'Carga Embarcada / Em Trânsito para Entrega');
    if (ok) await refreshData();
    setIsSaving(false);
    return ok;
  };

  const chegadaEntrega = async (osId: number): Promise<boolean> => {
    setIsSaving(true);
    const ok = await areaMotoristaService.atualizarStatusOS(osId, 2, 'Chegada no Destino / Iniciando Desembarque');
    if (ok) await refreshData();
    setIsSaving(false);
    return ok;
  };

  const finalizarEntrega = async (comprovante: ComprovanteEntregaData): Promise<boolean> => {
    setIsSaving(true);
    const ok = await areaMotoristaService.salvarComprovanteEntrega(comprovante);
    if (ok) {
      await refreshData();
      if (viagemAtiva?.id === comprovante.os_id) {
        setViagemAtiva(null);
      }
    }
    setIsSaving(false);
    return ok;
  };

  const registrarOcorrencia = async (ocorrencia: OcorrenciaItem): Promise<boolean> => {
    setIsSaving(true);
    const ok = await areaMotoristaService.registrarOcorrencia(ocorrencia);
    if (ok) await refreshData();
    setIsSaving(false);
    return ok;
  };

  const salvarVistoria = async (vistoria: VistoriaVeiculo): Promise<boolean> => {
    return await areaMotoristaService.salvarVistoria(vistoria);
  };

  // Carrega e monitora notificações do motorista
  const carregarNotificacoes = useCallback(async () => {
    if (!currentMotorista) return;
    try {
      const lista = await rastreamentoNotificacaoService.getNotificacoesMotorista(currentMotorista.id);
      setNotificacoes(lista);

      // Se houver notificação não lida recente, coloca em destaque
      const naoLidaRecente = lista.find(n => !n.lida);
      if (naoLidaRecente) {
        setNotificacaoPendenteAlerta(naoLidaRecente);
      }
    } catch (err) {
      console.error('Erro ao buscar notificações do motorista:', err);
    }
  }, [currentMotorista]);

  // Transmite localização GPS atual
  const enviarLocalizacaoGps = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    if (!currentMotorista) return { success: false, message: 'Nenhum motorista logado' };
    setTransmitindoGps(true);
    try {
      const res = await rastreamentoNotificacaoService.capturarEEnviarGPS(
        currentMotorista.id,
        viagemAtiva?.id
      );
      return res;
    } finally {
      setTransmitindoGps(false);
    }
  }, [currentMotorista, viagemAtiva]);

  // Dispensa notificação e marca como lida
  const dispensarAlerta = useCallback(async (id: number) => {
    await rastreamentoNotificacaoService.marcarNotificacaoComoLida(id);
    setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    setNotificacaoPendenteAlerta(null);
  }, []);

  // Escuta de eventos em tempo real via Supabase Realtime WebSocket
  useEffect(() => {
    if (!currentMotorista) return;
    carregarNotificacoes();

    // 1. Inscrição em Tempo Real na tabela 'notificacoes'
    const unsubscribeNotificacoes = rastreamentoNotificacaoService.inscreverNotificacoesRealtime(
      currentMotorista.id,
      (novaNotif) => {
        setNotificacoes(prev => [novaNotif, ...prev.filter(n => n.id !== novaNotif.id)]);
        setNotificacaoPendenteAlerta(novaNotif);

        // Se for solicitação de localização, já captura e transmite o GPS automaticamente!
        if (novaNotif.tipo === 'solicitacao_localizacao') {
          enviarLocalizacaoGps();
        }
      },
      (notifAtualizada) => {
        setNotificacoes(prev => prev.map(n => n.id === notifAtualizada.id ? notifAtualizada : n));
        if (notifAtualizada.lida) {
          setNotificacaoPendenteAlerta(prev => prev?.id === notifAtualizada.id ? null : prev);
        }
      }
    );

    // 2. Inscrição em Tempo Real na tabela 'ordem_de_servico'
    const unsubscribeOS = rastreamentoNotificacaoService.inscreverOrdensServicoRealtime(
      currentMotorista.id,
      () => {
        refreshData();
      }
    );

    // 3. Fallback de eventos de janela interna e polling leve de contingência (a cada 30s)
    const interval = setInterval(() => {
      carregarNotificacoes();
    }, 30000);

    const handleNovaNotificacao = (e: any) => {
      const notif = e.detail as NotificacaoItem;
      if (notif && notif.pessoa_id === currentMotorista.id) {
        setNotificacoes(prev => [notif, ...prev.filter(n => n.id !== notif.id)]);
        setNotificacaoPendenteAlerta(notif);

        if (notif.tipo === 'solicitacao_localizacao') {
          enviarLocalizacaoGps();
        }
      }
    };

    const handleSolicitacaoGps = (e: any) => {
      if (e.detail?.motoristaId === currentMotorista.id) {
        enviarLocalizacaoGps();
      }
    };

    window.addEventListener('nova_notificacao_operacional', handleNovaNotificacao);
    window.addEventListener('solicitacao_gps_enviada', handleSolicitacaoGps);

    return () => {
      unsubscribeNotificacoes();
      unsubscribeOS();
      clearInterval(interval);
      window.removeEventListener('nova_notificacao_operacional', handleNovaNotificacao);
      window.removeEventListener('solicitacao_gps_enviada', handleSolicitacaoGps);
    };
  }, [currentMotorista, carregarNotificacoes, enviarLocalizacaoGps, refreshData]);

  return (
    <AreaMotoristaContext.Provider
      value={{
        motoristasList,
        currentMotorista,
        setCurrentMotoristaId,
        isAgregado,
        semVinculo,
        stats,
        ordensServico,
        viagemAtiva,
        setViagemAtiva,
        isLoading,
        isSaving,
        refreshData,
        notificacoes,
        notificacaoPendenteAlerta,
        dispensarAlerta,
        enviarLocalizacaoGps,
        transmitindoGps,
        iniciarViagem,
        chegadaColeta,
        iniciarTransporte,
        chegadaEntrega,
        finalizarEntrega,
        registrarOcorrencia,
        salvarVistoria
      }}
    >
      {children}
    </AreaMotoristaContext.Provider>
  );
};

export const useAreaMotoristaOptional = () => {
  return useContext(AreaMotoristaContext);
};

export const useAreaMotorista = () => {
  const context = useContext(AreaMotoristaContext);
  if (!context) {
    throw new Error('useAreaMotorista deve ser utilizado dentro de um AreaMotoristaProvider');
  }
  return context;
};
